import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "mode";

export interface RecordMode {
    isEditing: boolean;
    startEdit: () => void;
    stopEdit: () => void;
}

/**
 * Read / edit mode of a read-first record page (CRM), kept in `?mode=edit`
 * so a refresh stays in edit mode. Create pages pass `alwaysEditing`.
 * Work surfaces (missions) edit directly and don't use this.
 */
const useRecordMode = (options?: { alwaysEditing?: boolean }): RecordMode => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isEditing = !!options?.alwaysEditing || searchParams.get(PARAM) === "edit";

    const setMode = useCallback((editing: boolean) => {
        setSearchParams((previous) => {
            const next = new URLSearchParams(previous);
            if (editing) next.set(PARAM, "edit");
            else next.delete(PARAM);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const startEdit = useCallback(() => setMode(true), [setMode]);
    const stopEdit = useCallback(() => setMode(false), [setMode]);

    return { isEditing, startEdit, stopEdit };
};

export default useRecordMode;
